<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Navigation from './Navigation.vue'
import MobileMenu from './MobileMenu.vue'
import ThemeToggle from '../ui/ThemeToggle.vue'

/*
  No bar. The chrome is a frame of fixed corner elements — wordmark top-left,
  controls top-right, and the section rail (Navigation) on the right edge.
  The old name-left/links-right strip read corporate; this reads like an
  instrument panel around the work.
*/
const isMenuOpen = ref(false)
const y = ref(0)

const scrolled = computed(() => y.value > 24)

function onScroll() {
  y.value = window.scrollY
}

onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
})
onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))
</script>

<template>
  <header>
    <a href="#home" class="wordmark" :data-scrolled="scrolled">
      <span class="sig" aria-hidden="true"></span>
      abraham&nbsp;michael
    </a>

    <div class="controls" :data-scrolled="scrolled">
      <ThemeToggle />
      <button
        class="burger"
        aria-label="Open menu"
        :aria-expanded="isMenuOpen"
        @click="isMenuOpen = true"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M4 8h16M4 16h16" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <Navigation />

    <MobileMenu :is-open="isMenuOpen" @close="isMenuOpen = false" />
  </header>
</template>

<style scoped>
.wordmark,
.controls {
  position: fixed;
  top: clamp(0.9rem, 2vh, 1.4rem);
  z-index: 65;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  border: 1px solid transparent;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}
.wordmark {
  left: clamp(0.75rem, 2vw, 2rem);
  font-family: var(--font-mono);
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--c-ink);
}
.controls {
  right: clamp(0.75rem, 2vw, 2rem);
  padding: 0.3rem;
}

/* floating over content: earn legibility only once there is content below */
.wordmark[data-scrolled='true'],
.controls[data-scrolled='true'] {
  background: color-mix(in srgb, var(--c-surface) 82%, transparent);
  backdrop-filter: blur(8px);
  border-color: var(--c-rule);
}

/* the mark: a signal chip, not a logotype */
.sig {
  width: 8px;
  height: 8px;
  flex: none;
  background: var(--c-accent);
  transition: rotate 0.4s var(--ease-out-quint);
}
.wordmark:hover .sig {
  rotate: 45deg;
}

.burger {
  display: none;
  width: 2.2rem;
  height: 2.2rem;
  place-items: center;
  background: none;
  border: 1px solid var(--c-rule);
  border-radius: 50%;
  color: var(--c-ink-2);
  cursor: pointer;
}
.burger svg {
  width: 1.05rem;
  height: 1.05rem;
}
.burger:hover {
  color: var(--c-accent);
  border-color: var(--c-accent);
}

/* the rail hides below 900px; the burger takes over at the same point */
@media (max-width: 899px) {
  .burger {
    display: grid;
  }
}
</style>
